import React, { useState } from 'react';
import { SettingsPage } from '../../components/SettingsPage/SettingsPage';
import { WorkspaceUpdateSettingsPage } from '../WorkspaceUpdateSettings/WorkspaceUpdateSettingsPage';
import { ShortcutSettingsPage } from '../ShortcutsSettings/ShortcutSettingsPage';
import { AboutSettingsPage } from '../AboutSettings/AboutSettingsPage';
import { GENERAL_SETTING_IDS } from '../../config/SettingIds';
import { FileDownloadLocationSetting } from '../../customSettings/FileDownloadLocationSetting/FileDownloadLocationSetting';
import { SessionReconnectSetting } from '../../customSettings/SessionReconnectSetting/SessionReconnectSetting';
import { SettingsCategory } from '../../config/SettingsCategory';
import { useSettingsPage } from '../../hooks/useSettingsPage';
import { SettingsRenderer } from '../../SettingsProvider/SettingsRenderer';

// Navigation views for General Settings
export enum GeneralSettingsView {
	General = 'general',
	WorkspaceUpdate = 'workspace-update',
	Shortcuts = 'shortcuts',
	About = 'about',
}

interface GeneralSettingsPageProps {
	title: string;
	description?: string;
}

export const GeneralSettingsPage: React.FC<GeneralSettingsPageProps> = ({ title }) => {
	const [currentView, setCurrentView] = useState<GeneralSettingsView>(
		GeneralSettingsView.General
	);

	const { settingsState, availableSettingIds, handleChangeBySettingId } = useSettingsPage(
		{ category: SettingsCategory.General, revertOnError: true }
	);

	const navigateToWorkspaceUpdateSettingsView = () => {
		setCurrentView(GeneralSettingsView.WorkspaceUpdate);
	};

	const navigateToGeneralSettingsView = () => {
		setCurrentView(GeneralSettingsView.General);
	};

	const navigateToShortcutSettingsView = () => {
		setCurrentView(GeneralSettingsView.Shortcuts);
	};

	const handleAboutClick = () => {
		setCurrentView(GeneralSettingsView.About);
	};

	// Custom components map for special settings
	const customComponents = {
		[GENERAL_SETTING_IDS.FILE_DOWNLOAD_LOCATION]: (
			<FileDownloadLocationSetting key={GENERAL_SETTING_IDS.FILE_DOWNLOAD_LOCATION} />
		),
		[GENERAL_SETTING_IDS.SESSION_RECONNECT]: (
			<SessionReconnectSetting key={GENERAL_SETTING_IDS.SESSION_RECONNECT} />
		),
	};

	// Navigation handlers map for navigation settings
	const navigationHandlers = {
		[GENERAL_SETTING_IDS.WORKSPACE_UPDATE]: navigateToWorkspaceUpdateSettingsView,
		[GENERAL_SETTING_IDS.SHORTCUTS]: navigateToShortcutSettingsView,
		[GENERAL_SETTING_IDS.ABOUT]: handleAboutClick,
	};

	// Use SettingsRenderer for all settings
	const renderSettingById = (settingId: string) => {
		return SettingsRenderer({
			settingId,
			settingsState,
			handleChange: handleChangeBySettingId,
			customComponents,
			navigationHandlers,
		});
	};

	// Configuration map for different views
	const viewConfig = {
		[GeneralSettingsView.WorkspaceUpdate]: {
			title: 'Citrix Workspace update',
			onNavigatingBack: navigateToGeneralSettingsView,
		},
		[GeneralSettingsView.Shortcuts]: {
			title: 'Shortcuts',
			onNavigatingBack: navigateToGeneralSettingsView,
		},
		[GeneralSettingsView.About]: {
			title: 'About',
			onNavigatingBack: navigateToGeneralSettingsView,
		},
	};

	if (currentView === GeneralSettingsView.WorkspaceUpdate) {
		const config = viewConfig[GeneralSettingsView.WorkspaceUpdate];
		return (
			<WorkspaceUpdateSettingsPage
				title={config.title}
				onNavigatingBack={config.onNavigatingBack}
			/>
		);
	}

	if (currentView === GeneralSettingsView.Shortcuts) {
		const config = viewConfig[GeneralSettingsView.Shortcuts];
		return (
			<ShortcutSettingsPage
				title={config.title}
				onNavigatingBack={config.onNavigatingBack}
			/>
		);
	}

	if (currentView === GeneralSettingsView.About) {
		const config = viewConfig[GeneralSettingsView.About];
		return (
			<AboutSettingsPage
				title={config.title}
				onNavigatingBack={config.onNavigatingBack}
			/>
		);
	}

	return (
		<SettingsPage
			title={title}
			settings={
				<>
					{/* Dynamically render all available settings */}
					{availableSettingIds.map((settingId) => renderSettingById(settingId))}
				</>
			}
		/>
	);
};
